import { useCallback, useRef } from 'react';

import { AxiosResponse } from 'axios';
import { ValueOf } from 'src/utils/types';

import PollClient from 'core/helpers/PollClient';
import { submitSubmission, initSubmissionPoll } from 'src/utils/services';

import {
  API_RESPONSE_STATUS,
  COSMOS_POLL_STATUS,
  DATA_SUBMISSION_STATUS,
  JOURNEY_TYPES,
} from '../../constants';
import { initiateCosmosPolling } from '../../helpers';

import { SubmissionParams } from '../../types';

export const useSubmissionSubmit = ({
  journeyType,
}: {
  journeyType: ValueOf<typeof JOURNEY_TYPES>;
}) => {
  const pollClientRef = useRef<PollClient<AxiosResponse> | null>(null);

  return useCallback(
    async ({
      dataType,
      submissionMode,
      payload,
      handleSuccess,
      handleFailure,
      handlePollResult,
      customPollingInterval,
    }: SubmissionParams) => {
      if (pollClientRef.current) {
        pollClientRef.current.abort();
        pollClientRef.current = null;
      }

      try {
        const res = await submitSubmission(
          journeyType,
          dataType,
          submissionMode,
          payload
        );

        const { data } = res;
        const { statusCode, result, status, error } = data || {};

        const shouldFail =
          statusCode !== 200 ||
          status !== API_RESPONSE_STATUS.SUCCESS ||
          !result ||
          (!result.poll &&
            result.dataSubmissionStatus !== DATA_SUBMISSION_STATUS.SUCCESS) ||
          (result.poll && !result.pollConfig?.pollId);

        if (shouldFail) {
          handleFailure({ message: error?.message });
          return;
        }

        if (
          !result.poll &&
          result.dataSubmissionStatus === DATA_SUBMISSION_STATUS.SUCCESS
        ) {
          handleSuccess(result);
          return;
        }

        const { maxAttempts, pollInterval, pollId } = result.pollConfig;

        // Use custom polling interval if provided, otherwise use API response interval
        const effectivePollingInterval = customPollingInterval || pollInterval;

        pollClientRef.current = initiateCosmosPolling<AxiosResponse<any>>({
          pollingApiFn: () => initSubmissionPoll(journeyType, pollId),
          maxAttempts: maxAttempts,
          pollInterval: effectivePollingInterval,
          continuePollingCondition: pollRes => {
            return (
              pollRes?.status === 200 &&
              pollRes?.data?.status.toLocaleLowerCase() ===
                API_RESPONSE_STATUS.SUCCESS.toLocaleLowerCase() &&
              pollRes?.data?.result?.pollStatus === COSMOS_POLL_STATUS.CONTINUE
            );
          },
          onPollSuccess: pollRes => {
            if (handlePollResult) {
              handlePollResult(pollRes);
              return;
            }

            const { data } = pollRes || {};
            const { status, result } = data || {};

            if (
              status === API_RESPONSE_STATUS.SUCCESS &&
              result?.pollStatus === COSMOS_POLL_STATUS.SUCCESS
            ) {
              handleSuccess(pollRes.data?.result);
            } else {
              handleFailure({ message: pollRes?.data?.error?.message });
            }
          },
          onPollFailed: error => {
            handleFailure(error);
          },
          onPollExpire: () => {
            handleFailure();
          },
        });
      } catch (error: any) {
        handleFailure(error);
      }
    },
    [journeyType]
  );
};
